import React, { useEffect, useState } from "react";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { createSelector } from "reselect";
import { userLogout, GetUserData, showToastMessage } from "../../common/service/CommonService";
import withRouter from "./withRouter";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import FilePreviewModal from "modules/Study/Attachments/components/FilePreviewModal";

/* ---------- Types ---------- */

interface ProfileState {
  user: {
    first_name?: string;
    [key: string]: any;
  };
}

interface ProfileDropdownProps {
  router: {
    navigate: (path: string) => void;
  };
}

interface UserData {
  userdisplayname?: string;
  iscompanysettingrr?: boolean;
  versionid?: number | string | null;
  versionname?: string;
}

/* ---------- Component ---------- */

const ProfileDropdown: React.FC<ProfileDropdownProps> = (props) => {
  const profiledropdownData = createSelector(
    (state: { Profile: ProfileState }) => state.Profile,
    (profile) => profile.user
  );

  const user = useSelector(profiledropdownData);

  const [userName, setUserName] = useState<string>("Admin");

  const [profilePicLetter, setProfilePicLetter] = useState<string>("Z");
  const [userDisplayName, setUserDisplayName] =
    useState<string>("Zentixs User");
  const [branName, setBranName] = useState<string | null>(null);
  const [roleName, setRoleName] = useState<string | null>(null);

  const [isProfileDropdown, setIsProfileDropdown] =
    useState<boolean>(false);

  const [isVersionSummaryOpen, setIsVersionSummaryOpen] =
    useState<boolean>(false);

  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const [sampleCollectionMethod, setSampleCollectionMethod] = useState<string>("scan_sheet");

  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [activeReportType, setActiveReportType] = useState<string | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  /* ---------- Effects ---------- */

  useEffect(() => {
    const authUser = sessionStorage.getItem("authUser");
    if (!authUser) return;

    const obj: any = JSON.parse(authUser);

    setUserName(
      process.env.REACT_APP_DEFAULTAUTH === "fake"
        ? obj.username ??
        user?.first_name ??
        obj?.data?.first_name ??
        "Admin"
        : process.env.REACT_APP_DEFAULTAUTH === "firebase"
          ? obj?.email ?? "Admin"
          : "Admin"
    );
  }, [user]);

  useEffect(() => {
    const userData: UserData | null = GetUserData();

    setProfilePicLetter(userData?.userdisplayname?.charAt(0) || "Z");
    setUserDisplayName(userData?.userdisplayname || "Zentixs User");
    setBranName(sessionStorage.getItem("branName"));
    setRoleName(sessionStorage.getItem("role"));

    const savedMethod = sessionStorage.getItem("sampleCollectionMethod") || "scan_sheet";
    setSampleCollectionMethod(savedMethod);
  }, []);

  /* ---------- Handlers ---------- */

  const toggleProfileDropdown = (): void => {
    setIsProfileDropdown((prev) => !prev);
  };

  const toggleVersionSummaryOpen = (): void => {
    setIsVersionSummaryOpen((prev) => !prev);
  };

  const openVersionSummaryModal = (): void => {
    toggleVersionSummaryOpen();
  };

  const handleAuditReportClick = (e: React.MouseEvent, moduleName: string) => {
    e.preventDefault();
    if (AuditMutation.isPending) return;

    setActiveReportType(moduleName);
    const payload = {
      modulename: moduleName,
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        setActiveReportType(null);
        if (data instanceof Blob) {
          const pdfFile = new File([data], `${moduleName.replace(/\s+/g, "_")}_Report.pdf`, {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        } else {
          showToastMessage("error", `Failed to generate ${moduleName} report`);
        }
      },
      onError: (error: any) => {
        setActiveReportType(null);
        const errorMessage =
          typeof error === "string"
            ? error
            : (error?.message || `Error fetching ${moduleName} report`);
        showToastMessage("error", errorMessage);
        console.error(`Error fetching ${moduleName} audit report:`, error);
      },
    });
  };

  const userData: UserData | null = GetUserData();
  const { isModuleVisible,hasPermission } = usePermission();

  return (
    <>
      <Dropdown
        isOpen={isProfileDropdown}
        toggle={toggleProfileDropdown}
        className="header-item profile_dropdown"
        direction="end"
      >
        <DropdownToggle tag="button" type="button" className="btn w-100">
          <span className="d-flex align-items-center">
            <div className="avatar-xs avatar_logo">
              <div className="avatar-title rounded-circle bg-light text-primary fw-semibold fs-6 text-uppercase">
                {profilePicLetter}
              </div>
            </div>

            <span className="text-start ms-xl-2 username overflow-ellipsis">
              <span className="ms-1 fw-medium text-white user-name-text">
                {userDisplayName}
              </span>
              <p className="ms-1 text-muted mb-0 user-name-sub-text">
                {roleName && roleName !== "null" ? roleName : "-"} -{" "}
                {branName && branName !== "null" ? branName : "-"}
              </p>
            </span>
          </span>
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu-end bg-secondary">
          <DropdownItem className="p-0" style={{ pointerEvents: "none" }}>
            <div className="dropdown-item">
              <i className="mdi mdi-account-outline text-white fs-16 me-1"></i>
              <span className="text-white fw-bold">
                {userDisplayName}
              </span>
            </div>
          </DropdownItem>
          {hasPermission(PERMISSION_MODULES.SETTINGS, PERMISSION_ACTIONS.MANAGE_SETTINGS) && (isModuleVisible("SETTINGS")) && (
            <DropdownItem className="p-0">
              <Link
                to="/settings"
                className="dropdown-item"
              >
                <i className="mdi mdi-cog-outline text-white fs-16 me-1"></i>
                <span className="text-white">Settings</span>
              </Link>
            </DropdownItem>
          )}

          {hasPermission(PERMISSION_MODULES.MODULE_MANAGEMENT, PERMISSION_ACTIONS.MANAGE_MODULES) && (isModuleVisible("MODULE_MANAGEMENT")) && (
            <DropdownItem className="p-0">
              <Link
                to="/feature-activation"
                className="dropdown-item"
              >
                <i className="mdi mdi-toggle-switch-outline text-white fs-16 me-1"></i>
                <span className="text-white">Module Management</span>
              </Link>
            </DropdownItem>
          )}

          <DropdownItem className="p-0">
            <Link
              to={`${process.env.PUBLIC_URL}/change-password`}
              className="dropdown-item"
            >
              <i className="mdi mdi-lock-open-outline text-white fs-16 me-1"></i>
              <span className="text-white">Change Password</span>
            </Link>
          </DropdownItem>

          {/* <DropdownItem className="p-0">
            <a
              href="#"
              onClick={(e) => handleAuditReportClick(e, "Login & Logout")}
              className="dropdown-item d-flex align-items-center justify-content-between"
              style={{ cursor: AuditMutation.isPending ? "not-allowed" : "pointer" }}
            >
              <div className="d-flex align-items-center">
                <i className="mdi mdi-shield-account-outline text-white fs-16 me-1"></i>
                <span className="text-white">Login & Logout Report</span>
              </div>
              {AuditMutation.isPending && activeReportType === "login & logout" && (
                <Spinner size="sm" color="light" />
              )}
            </a>
          </DropdownItem> */}

          {/* <DropdownItem className="d-flex align-items-center justify-content-between text-white">
          <div className="d-flex align-items-center">
            <i className="mdi mdi-information-outline fs-16 me-2"></i>
            <span>Version</span>
          </div>

          {userData?.versionid && (
            <span className="fs-12 text-white-50">
              {userData.versionname}
            </span>
          )}
        </DropdownItem> */}

          {userData?.versionid && (
            <DropdownItem className="p-0">
              <Link
                onClick={openVersionSummaryModal}
                className="dropdown-item text-success"
                to="#"
              >
                Release Notes
              </Link>
            </DropdownItem>
          )}

          <DropdownItem className="p-0">
            <a
              onClick={() => userLogout(props.router.navigate)}
              className="dropdown-item"
            >
              <i className="mdi mdi-logout text-white fs-16 me-1"></i>
              <span className="text-white">Logout</span>
            </a>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size="xl"
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </>
  );
};

export default withRouter(ProfileDropdown);
